import React, { useEffect, useState } from 'react';
import { View, Text, ScrollView, Pressable, ActivityIndicator, Modal, StyleSheet, useWindowDimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Container, ContainerInputs, ContainerMockup, ContainerLayout } from './style';
import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { firestore } from '../../services/firebase';
import MockupSmartphone from '../../components/MockupSmartphone';
import Header from '../../components/Header';
import { useNavigation } from '@react-navigation/native';
import { useAuthUser } from '../../contexts/useAuthUser';
import FlatListMultipleSelection from '../ClickCard/FlatListMultipleSelection';
import AdminInput from '../../components/AdminInput';
import AdminAlert from '../../components/AdminAlert';
import AdminSwitch from '../../components/AdminSwitch';
import AdminDatePicker from '../../components/AdminDatePicker';
import HoverTooltip from '../../components/HoverTooltip';
import { ADMIN_COLORS, ADMIN_FONT } from '../../config/theme/adminColors';
import { SectionCard, Badge, SegmentedControl } from '../AdminPanel/Products/formStyles';
import {
  translateNotificationError,
  isFailureStatus,
  formatDeliveryCounts,
} from '../../utils/notificationStatus';
import { toDateSafe } from '../../utils/dateUtils';
import {
  describeNearbySend,
  maskTimeInput,
  normalizeTimeInput,
  validateSchedule,
  formatScheduleDateTime,
  describeTimeUntil,
  groupByDay,
  parseScheduleDateTime,
  previewOccurrences,
  describeRecurrence,
  advanceSchedule,
  endOfDayMs,
} from '../../utils/notificationScheduling';

const MAX_TITLE_LENGTH = 65;
const MAX_MESSAGE_LENGTH = 178;

// Hoje em ISO local — data mínima do calendário. O padrão do AdminDatePicker é
// amanhã, mas agendar para daqui a algumas horas é justamente o caso mais comum.
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
};

// Como o envio de cada notificação aparece na lista de enviadas.
const STATUS_META = {
  success: { label: 'Enviada', variant: 'success' },
  pending: { label: 'Na fila', variant: 'neutral' },
  processing: { label: 'Enviando', variant: 'info' },
  // Aguardando a data programada pelo admin (disparo pelo cron do backend).
  scheduled: { label: 'Agendada', variant: 'info' },
  // Série que chegou ao fim do período de repetição.
  completed: { label: 'Série encerrada', variant: 'neutral' },
  // Parte dos dispositivos recebeu e parte não (ex.: app desinstalado). Antes
  // esse caso era gravado como "Enviada", escondendo a entrega incompleta.
  partial: { label: 'Enviada em parte', variant: 'info' },
  error: { label: 'Falhou', variant: 'danger' },
  failed: { label: 'Falhou', variant: 'danger' },
};

const formatSendDate = (value) => {
  const date = toDateSafe(value);
  if (!date) return '—';
  return `${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  })}`;
};

const Notifications = () => {
  // Tela de Stack, sem cabeçalho do navegador: sem isto não existe caminho de
  // volta nenhum — só o botão do navegador. `Admin` é a raiz do painel, usada
  // quando não há histórico (link aberto direto).
  const navigation = useNavigation();
  const { user } = useAuthUser();
  const voltar = () =>
    navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Admin');

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [allProducts, setAllProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deeplink, setDeeplink] = useState('');
  const [isDeeplink, setIsDeeplink] = useState(false);
  const [sendAllUser, setSendAllUsers] = useState(false);
  const [sendFilteredUser, setSendFilteredUsers] = useState(false);
  // `useWindowDimensions` já reage a resize sozinho — o listener manual de
  // Dimensions que existia aqui virava estado duplicado sem necessidade.
  const { width: screenWidth } = useWindowDimensions();
  const isVisible = screenWidth > 1024;
  const isMobile = screenWidth < 768;

  // Quando enviar: agora ou numa data programada.
  const [sendMode, setSendMode] = useState('now'); // 'now' | 'schedule'
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  // Repetição da série: 'once' | 'daily' | 'weekly' | 'monthly'.
  const [frequency, setFrequency] = useState('once');
  const [untilDate, setUntilDate] = useState(''); // '' = repete sem data de fim

  // Agendamento que está para ser excluído (abre a confirmação).
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Aba ativa, fila de agendadas e histórico de envios.
  const [tab, setTab] = useState('compose'); // 'compose' | 'scheduled' | 'history'
  const [sentNotifications, setSentNotifications] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [scheduledNotifications, setScheduledNotifications] = useState([]);
  const [loadingScheduled, setLoadingScheduled] = useState(true);
  const [skippingId, setSkippingId] = useState(null);

  const fetchSentNotifications = async () => {
    setLoadingHistory(true);
    try {
      // orderBy num campo simples de coleção usa o índice automático do
      // Firestore: não precisa de índice composto criado à mão.
      //
      // As agendadas não aparecem aqui: elas só ganham `sendDate` quando saem
      // de fato, e o Firestore omite de um orderBy os docs sem o campo. Assim o
      // histórico continua sendo só o que já foi disparado.
      const snapshot = await getDocs(
        query(collection(firestore, 'notifications'), orderBy('sendDate', 'desc'), limit(100))
      );
      setSentNotifications(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Erro ao carregar as notificações enviadas:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  // Fila do que ainda vai sair. Filtro só de igualdade (índice automático) e
  // ordenação em memória — o backend usa exatamente a mesma consulta.
  const fetchScheduledNotifications = async () => {
    setLoadingScheduled(true);
    try {
      const snapshot = await getDocs(
        query(collection(firestore, 'notifications'), where('status', '==', 'scheduled'))
      );
      setScheduledNotifications(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Erro ao carregar as notificações agendadas:', err);
    } finally {
      setLoadingScheduled(false);
    }
  };

  useEffect(() => {
    fetchSentNotifications();
    fetchScheduledNotifications();
  }, []);

  // Horários já ocupados, usados só para AVISAR que há um envio por perto —
  // nada aqui bloqueia o agendamento. Entram a fila de agendados e os envios já
  // realizados.
  //
  // Numa série, só a PRÓXIMA ocorrência é conferida: as futuras nem existem
  // como documento ainda. Duas séries no mesmo horário são pegas (as duas
  // apontam para a mesma próxima data); combinações mais raras, como uma diária
  // que só daqui a semanas encosta numa semanal, passam sem aviso. Como é só um
  // aviso, o custo de deixar passar é zero — os dois envios saem em fila de
  // qualquer forma.
  const scheduledDates = [
    ...scheduledNotifications.map((item) => toDateSafe(item.scheduledFor)),
    ...sentNotifications.map((item) => toDateSafe(item.sendDate)),
  ].filter(Boolean);

  // Primeira ocorrência do que está sendo montado no formulário. É o que dá
  // nome à repetição ("Toda segunda-feira" depende da data escolhida) e o que
  // alimenta a prévia — null enquanto data ou horário não estiverem completos.
  const firstDate = parseScheduleDateTime(scheduleDate, scheduleTime);
  const previewDates =
    sendMode === 'schedule' && firstDate
      ? previewOccurrences({
          start: firstDate,
          frequency,
          untilMs: untilDate ? endOfDayMs(untilDate) : null,
          count: 3,
        })
      : [];

  // Aviso, não bloqueio: a entrega é serializada numa fila, então dois envios
  // próximos saem um após o outro. Só vale a pena o admin conferir se é isso
  // mesmo que ele quer que o aluno receba.
  const nearbyWarning = firstDate ? describeNearbySend(firstDate, scheduledDates) : '';

  useEffect(() => {
    const fetchProducts = async () => {
      const response = await getDocs(collection(firestore, 'products'));
      const products = response.docs.map(doc => {
        const data = doc.data();
        return {
          productId: data.productId || doc.id,
          name: data.name,
          marketplace: data?.marketplace || 'STRIPE',
          selected: false,
        };
      });
      setAllProducts(products);
    };

    fetchProducts();
  }, []);

  const formatDeeplink = (raw) => {
    const input = (raw || '').trim();
    if (!input) { setDeeplink(''); return; }
  
    const decoded = decodeURIComponent(input);
  
    // 1) Se vier no formato /community/<id>, normaliza para CommunityComments?id=<id>
    const mCommunity = decoded.match(/\/community\/([^/?#]+)/i);
    if (mCommunity) {
      const id = mCommunity[1];
      setDeeplink(`CommunityComments?id=${encodeURIComponent(id)}`);
      return;
    }
  
    // 2) Extrai rota e query de: "https://dominio/Live?..." | "myapp://Modulo?..." | "Aula?..."
    const [rawPath, rawQuery = ''] = decoded.split('?');
    const lastSeg = (rawPath.split('/').filter(Boolean).pop() || '').toLowerCase();
  
    // 3) Roteamento permitido
    const allowed = {
      aula: 'Aula',
      modulo: 'Modulo',
      live: 'Live',
      communitycomments: 'CommunityComments',
    };
  
    let routeKey = lastSeg;
  
    // fallback quando o admin cola diretamente "Aula?..." (sem barra)
    if (!allowed[routeKey]) {
      const direct = (rawPath || '').toLowerCase();
      if (allowed[direct]) routeKey = direct;
    }
  
    if (!allowed[routeKey]) {
      // rota não suportada → zera para evitar salvar lixo
      setDeeplink('');
      return;
    }
  
    const route = allowed[routeKey];
    const final = rawQuery ? `${route}?${rawQuery}` : route;
    setDeeplink(final);
  };

  // Limpa o formulário depois de enviar ou agendar.
  //
  // Depois de AGENDAR, o modo e a data escolhidos ficam de pé: programar as
  // comunicações de um dia ou de uma semana de uma vez é justamente o caso de
  // uso, e voltar para "Agora" a cada notificação obrigaria a refazer as duas
  // escolhas toda vez. O horário sempre é zerado — ele é o que muda entre um
  // envio e outro, e cada novo valor passa pela validação de folga mínima.
  const resetForm = ({ keepSchedule = false } = {}) => {
    setTitle('');
    setContent('');
    setDeeplink('');
    setSendAllUsers(false);
    setSendFilteredUsers(false);
    setIsDeeplink(false);
    setScheduleTime('');
    if (!keepSchedule) {
      setSendMode('now');
      setScheduleDate('');
      setFrequency('once');
      setUntilDate('');
    }
    setAllProducts(prev => prev.map(p => ({ ...p, selected: false })));
  };

  // Campos comuns aos dois modos. `scheduledFor` decide o resto: o backend
  // ignora no onCreate tudo que não nasce 'pending', então um doc 'scheduled'
  // fica esperando o cron em vez de sair na hora.
  const buildNotificationData = (scheduledFor) => {
    const base = {
      title,
      body: content,
      type: 'schedulingAdmin',
      productsIds: allProducts.filter(e => e.selected).map(e => e.productId),
      deeplink: deeplink || '',
    };

    if (!scheduledFor) return { ...base, status: 'pending', sendDate: new Date() };

    // Sem `sendDate`: ele é gravado quando a notificação sai de fato, e é isso
    // que mantém a agendada fora do histórico de enviadas.
    const data = {
      ...base,
      status: 'scheduled',
      scheduledFor,
      createdAt: serverTimestamp(),
    };

    // `recurrence` ausente = envio único. Numa série, `scheduledFor` é sempre a
    // PRÓXIMA ocorrência: o backend a avança a cada disparo.
    if (frequency !== 'once') {
      const untilMs = untilDate ? endOfDayMs(untilDate) : null;
      data.recurrence = {
        frequency,
        until: untilMs === null ? null : new Date(untilMs),
      };
    }

    return data;
  };

  const handleSendNotification = async (naFrente = 0) => {
    setLoading(true);
    try {
      await addDoc(collection(firestore, 'notifications'), buildNotificationData(null));
      resetForm();
      setError('');
      setSuccess(
        naFrente > 0
          ? `Notificação na fila: ${naFrente} ${naFrente === 1 ? 'envio' : 'envios'} na frente. Ela sai assim que chegar a vez.`
          : 'Notificação enviada com sucesso!'
      );
      fetchSentNotifications(); // a nova aparece no histórico na hora
    } catch (err) {
      setSuccess('');
      setError('Erro ao enviar a notificação.');
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleNotification = async (date) => {
    setLoading(true);
    try {
      await addDoc(collection(firestore, 'notifications'), buildNotificationData(date));
      resetForm({ keepSchedule: true });
      setError('');
      setSuccess(
        frequency === 'once'
          ? `Notificação agendada para ${formatScheduleDateTime(date)}.`
          : `Série criada: ${describeRecurrence(frequency, date).toLowerCase()}, a partir de ${formatScheduleDateTime(date)}.`
      );
      fetchScheduledNotifications(); // a nova entra na fila na hora
    } catch (err) {
      console.error('Erro ao agendar a notificação:', err);
      setSuccess('');
      setError('Erro ao agendar a notificação.');
    } finally {
      setLoading(false);
    }
  };

  // Regras que valem para os dois modos. Devolve false quando já mostrou o erro.
  const validateMessage = () => {
    if (!title || !content) {
      setError('Título e conteúdo são obrigatórios.');
      return false;
    }

    if (!sendAllUser && !sendFilteredUser) {
      setError('Selecione os destinatários da notificação.');
      return false;
    }

    if (sendFilteredUser && !allProducts.some(p => p.selected)) {
      setError('Selecione ao menos um produto.');
      return false;
    }

    return true;
  };

  const handleSend = async () => {
    setSuccess('');
    if (!validateMessage()) return;

    if (sendMode === 'schedule') {
      const { date, error: scheduleError } = validateSchedule({
        dateIso: scheduleDate,
        timeText: scheduleTime,
        frequency,
        untilIso: untilDate,
      });

      if (scheduleError) {
        setError(scheduleError);
        return;
      }

      setError('');
      handleScheduleNotification(date);
      return;
    }

    // Não existe mais trava de tempo entre envios: o admin dispara quantos
    // quiser em sequência. Esta consulta não decide SE envia, só QUANTOS estão
    // na frente — o backend serializa a entrega e um sai depois do outro.
    try {
      const emAndamento = await getDocs(
        query(
          collection(firestore, 'notifications'),
          where('status', 'in', ['pending', 'processing']),
          limit(20)
        )
      );

      setError('');
      handleSendNotification(emAndamento.size);
    } catch (err) {
      // Sem saber o tamanho da fila, o envio segue assim mesmo: a contagem é
      // só para a mensagem, e travar o disparo por causa dela seria pior.
      console.error('Erro ao consultar a fila de notificações:', err);
      setError('');
      handleSendNotification();
    }
  };

  // Exclui um agendamento. Numa série, apaga a série inteira: os envios que ela
  // já produziu são documentos separados e continuam no histórico.
  //
  // A transação relê o status atual porque o cron pode ter reivindicado o envio
  // (scheduled -> processing) nesse meio-tempo. Apagar aí seria pior do que
  // recusar: a notificação já está a caminho dos alunos e a função ainda vai
  // gravar o resultado nesse documento.
  const handleDeleteSchedule = async (item) => {
    setDeleting(true);
    setError('');
    setSuccess('');

    try {
      await runTransaction(firestore, async (transaction) => {
        const ref = doc(firestore, 'notifications', item.id);
        const snapshot = await transaction.get(ref);

        if (!snapshot.exists()) throw new Error('not-found');
        if (snapshot.data().status !== 'scheduled') throw new Error('already-dispatched');

        transaction.delete(ref);
      });

      setSuccess(
        item.recurrence?.frequency
          ? 'Série excluída. Os envios já realizados continuam no histórico.'
          : 'Agendamento excluído.'
      );
      setPendingDelete(null);
    } catch (err) {
      if (err?.message === 'already-dispatched') {
        setError('Esta notificação já começou a ser enviada e não pode mais ser excluída.');
      } else if (err?.message === 'not-found') {
        setError('Este agendamento não existe mais.');
      } else {
        console.error('Erro ao excluir o agendamento:', err);
        setError('Erro ao excluir o agendamento.');
      }
      setPendingDelete(null);
    } finally {
      setDeleting(false);
      fetchScheduledNotifications();
      fetchSentNotifications();
    }
  };

  // Pula só o próximo envio de uma série, empurrando-a para a data seguinte —
  // o jeito de tirar um feriado do caminho sem desmontar e refazer a série.
  const handleSkipNext = async (item) => {
    setSkippingId(item.id);
    setError('');
    setSuccess('');

    try {
      // `advanceSchedule` (e não um passo simples) porque a data seguinte
      // precisa cair no FUTURO: se o envio já venceu e está só esperando a
      // próxima rodada do cron, avançar um passo poderia deixá-lo ainda no
      // passado — e o cron mandaria justamente o envio que era para pular.
      // Devolve null quando não há mais data dentro do período da série.
      const depois = advanceSchedule({
        from: toDateSafe(item.scheduledFor),
        frequency: item.recurrence?.frequency,
        untilMs: toDateSafe(item.recurrence?.until)?.getTime() ?? null,
      });

      await runTransaction(firestore, async (transaction) => {
        const ref = doc(firestore, 'notifications', item.id);
        const snapshot = await transaction.get(ref);

        if (!snapshot.exists()) throw new Error('not-found');
        if (snapshot.data().status !== 'scheduled') throw new Error('already-dispatched');

        // Pular o último envio encerra a série, em vez de deixá-la agendada
        // para uma data que já passou do término.
        transaction.update(ref, depois ? { scheduledFor: depois } : { status: 'completed' });
      });

      setSuccess(
        depois
          ? `Envio pulado. O próximo será ${formatScheduleDateTime(depois)}.`
          : 'Último envio pulado. A série foi encerrada.'
      );
    } catch (err) {
      if (err?.message === 'already-dispatched') {
        setError('Este envio já começou e não pode mais ser pulado.');
      } else if (err?.message === 'not-found') {
        setError('Este agendamento não existe mais.');
      } else if (err?.message === 'no-next') {
        setError('Não foi possível calcular a próxima data desta série.');
      } else {
        console.error('Erro ao pular o envio:', err);
        setError('Erro ao pular o envio.');
      }
    } finally {
      setSkippingId(null);
      fetchScheduledNotifications();
    }
  };

  // Nome legível dos produtos de uma notificação já enviada. `productsIds`
  // vazio/ausente = envio para todos os alunos.
  const describeAudience = (productsIds) => {
    if (!Array.isArray(productsIds) || productsIds.length === 0) return 'Todos os alunos';
    const names = productsIds.map(
      (id) => allProducts.find((p) => p.productId === id)?.name || id
    );
    return names.join(', ');
  };

  const renderTab = (key, label, count) => {
    const active = tab === key;
    return (
      <Pressable
        onPress={() => setTab(key)}
        style={({ hovered }) => [
          styles.tab,
          active && styles.tabActive,
          hovered && !active && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
        ]}
      >
        <Text style={[styles.tabText, active && styles.tabTextActive]}>
          {label}
          {typeof count === 'number' ? ` (${count})` : ''}
        </Text>
      </Pressable>
    );
  };

  // Fila do que ainda vai sair, agrupada por dia — é a leitura que permite
  // programar as comunicações de uma semana ou de um mês e conferir tudo junto.
  const renderScheduled = () => {
    const grupos = groupByDay(scheduledNotifications, (item) => toDateSafe(item.scheduledFor));
    const semData = scheduledNotifications.length - grupos.reduce((n, g) => n + g.items.length, 0);

    return (
      <View style={{ padding: 24 }}>
        <SectionCard
          title="Notificações agendadas"
          subtitle={
            loadingScheduled
              ? 'Carregando…'
              : scheduledNotifications.length === 0
                ? 'Nada programado no momento.'
                : `${scheduledNotifications.length} ${scheduledNotifications.length === 1 ? 'envio programado' : 'envios programados'}`
          }
          right={
            <Pressable
              onPress={fetchScheduledNotifications}
              disabled={loadingScheduled}
              style={({ hovered }) => [
                styles.ghostBtn,
                hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
              ]}
            >
              <Ionicons name="refresh" size={16} color={ADMIN_COLORS.ink} />
              <Text style={styles.ghostBtnText}>Atualizar</Text>
            </Pressable>
          }
        >
          {loadingScheduled ? (
            <View style={{ paddingVertical: 40, alignItems: 'center' }}>
              <ActivityIndicator color={ADMIN_COLORS.accent} />
            </View>
          ) : scheduledNotifications.length === 0 ? (
            <Text style={styles.emptyText}>
              Nenhuma notificação agendada. Na aba "Nova notificação", escolha "Agendar" para
              programar os envios dos próximos dias de uma vez só.
            </Text>
          ) : (
            <View style={{ gap: 22 }}>
              {semData > 0 ? (
                <AdminAlert variant="warn">
                  {semData === 1
                    ? '1 agendamento está sem data válida e não será enviado. Cancele e crie de novo.'
                    : `${semData} agendamentos estão sem data válida e não serão enviados. Cancele e crie de novo.`}
                </AdminAlert>
              ) : null}

              {grupos.map((grupo) => (
                <View key={grupo.key} style={{ gap: 10 }}>
                  <Text style={styles.dayHeading}>{grupo.label}</Text>

                  {grupo.items.map((item) => {
                    const quando = toDateSafe(item.scheduledFor);
                    const repeticao = item.recurrence?.frequency;
                    const fim = toDateSafe(item.recurrence?.until);
                    const pulando = skippingId === item.id;

                    return (
                      <View key={item.id} style={styles.historyRow}>
                        <View style={styles.historyHeader}>
                          <Text style={styles.historyTitle} numberOfLines={2}>
                            {item.title || 'Sem título'}
                          </Text>
                          <Badge variant="info">{describeTimeUntil(quando)}</Badge>
                        </View>

                        {item.body ? (
                          <Text style={styles.historyBody} numberOfLines={3}>
                            {item.body}
                          </Text>
                        ) : null}

                        <View style={styles.historyMeta}>
                          <View style={styles.historyMetaItem}>
                            <Ionicons name="time-outline" size={13} color={ADMIN_COLORS.muted} />
                            <Text style={styles.historyMetaText}>
                              {formatScheduleDateTime(quando)}
                            </Text>
                          </View>
                          {repeticao ? (
                            <View style={styles.historyMetaItem}>
                              <Ionicons name="repeat" size={13} color={ADMIN_COLORS.muted} />
                              <Text style={styles.historyMetaText}>
                                {describeRecurrence(repeticao, quando)}
                                {fim ? ` até ${fim.toLocaleDateString('pt-BR')}` : ''}
                              </Text>
                            </View>
                          ) : null}
                          <View style={styles.historyMetaItem}>
                            <Ionicons name="people-outline" size={13} color={ADMIN_COLORS.muted} />
                            <Text style={styles.historyMetaText} numberOfLines={1}>
                              {describeAudience(item.productsIds)}
                            </Text>
                          </View>
                        </View>

                        <View style={styles.scheduledActions}>
                          {repeticao ? (
                            <Pressable
                              onPress={() => handleSkipNext(item)}
                              disabled={pulando}
                              style={({ hovered }) => [
                                styles.ghostBtn,
                                hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
                                pulando && { opacity: 0.6 },
                              ]}
                            >
                              {pulando ? (
                                <ActivityIndicator size="small" color={ADMIN_COLORS.ink} />
                              ) : (
                                <>
                                  <Ionicons
                                    name="play-skip-forward-outline"
                                    size={16}
                                    color={ADMIN_COLORS.ink}
                                  />
                                  <Text style={styles.ghostBtnText}>Pular este envio</Text>
                                </>
                              )}
                            </Pressable>
                          ) : null}

                          <Pressable
                            onPress={() => setPendingDelete(item)}
                            style={({ hovered }) => [
                              styles.ghostBtn,
                              styles.dangerBtn,
                              hovered && { backgroundColor: ADMIN_COLORS.dangerSoft },
                            ]}
                          >
                            <Ionicons name="trash-outline" size={16} color={ADMIN_COLORS.danger} />
                            <Text style={[styles.ghostBtnText, { color: ADMIN_COLORS.danger }]}>
                              {repeticao ? 'Excluir série' : 'Excluir'}
                            </Text>
                          </Pressable>
                        </View>
                      </View>
                    );
                  })}
                </View>
              ))}
            </View>
          )}
        </SectionCard>
      </View>
    );
  };

  // Confirmação da exclusão. Vale a interrupção: um envio programado some sem
  // volta, e numa série somem TODOS os envios futuros de uma vez.
  const renderDeleteConfirm = () => {
    if (!pendingDelete) return null;

    const repeticao = pendingDelete.recurrence?.frequency;
    const quando = toDateSafe(pendingDelete.scheduledFor);

    return (
      <Modal
        transparent
        animationType="fade"
        visible
        onRequestClose={() => (deleting ? null : setPendingDelete(null))}
      >
        <View style={styles.modalBackdrop}>
          <View style={styles.modalCard}>
            <Text style={styles.modalTitle}>
              {repeticao ? 'Excluir a série inteira?' : 'Excluir este agendamento?'}
            </Text>

            <Text style={styles.modalText}>
              <Text style={{ fontWeight: '700' }}>{pendingDelete.title || 'Sem título'}</Text>
              {repeticao
                ? ` — ${describeRecurrence(repeticao, quando).toLowerCase()}, a partir de ${formatScheduleDateTime(quando)}. Todos os envios futuros desta série são cancelados; os que já saíram continuam no histórico.`
                : ` — ${formatScheduleDateTime(quando)}. Esta notificação não será enviada.`}
            </Text>

            <View style={styles.modalActions}>
              <Pressable
                onPress={() => setPendingDelete(null)}
                disabled={deleting}
                style={({ hovered }) => [
                  styles.ghostBtn,
                  hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
                ]}
              >
                <Text style={styles.ghostBtnText}>Manter agendamento</Text>
              </Pressable>

              <Pressable
                onPress={() => handleDeleteSchedule(pendingDelete)}
                disabled={deleting}
                style={({ hovered }) => [
                  styles.dangerSolidBtn,
                  hovered && { opacity: 0.9 },
                  deleting && { opacity: 0.7 },
                ]}
              >
                {deleting ? (
                  <ActivityIndicator size="small" color="#FFFFFF" />
                ) : (
                  <>
                    <Ionicons name="trash-outline" size={16} color="#FFFFFF" />
                    <Text style={styles.dangerSolidBtnText}>
                      {repeticao ? 'Excluir série' : 'Excluir'}
                    </Text>
                  </>
                )}
              </Pressable>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  const renderHistory = () => (
    <View style={{ padding: 24 }}>
      <SectionCard
        title="Notificações enviadas"
        subtitle={
          loadingHistory
            ? 'Carregando…'
            : `${sentNotifications.length} ${sentNotifications.length === 1 ? 'envio registrado' : 'envios registrados'}${sentNotifications.length === 100 ? ' (100 mais recentes)' : ''}`
        }
        right={
          <Pressable
            onPress={fetchSentNotifications}
            disabled={loadingHistory}
            style={({ hovered }) => [
              styles.ghostBtn,
              hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
            ]}
          >
            <Ionicons name="refresh" size={16} color={ADMIN_COLORS.ink} />
            <Text style={styles.ghostBtnText}>Atualizar</Text>
          </Pressable>
        }
      >
        {loadingHistory ? (
          <View style={{ paddingVertical: 40, alignItems: 'center' }}>
            <ActivityIndicator color={ADMIN_COLORS.accent} />
          </View>
        ) : sentNotifications.length === 0 ? (
          <Text style={styles.emptyText}>
            Nenhuma notificação enviada ainda. Crie a primeira na aba "Nova notificação".
          </Text>
        ) : (
          <View style={{ gap: 10 }}>
            {sentNotifications.map((item) => {
              const status = STATUS_META[item.status] || {
                label: item.status || '—',
                variant: 'neutral',
              };
              // Doc antigo, de antes de a função gravar errorCode/errorMessage,
              // ainda mostra o hover — só sem detalhe nenhum para traduzir.
              const errorText = isFailureStatus(item.status)
                ? translateNotificationError(item.errorCode, item.errorMessage)
                : null;
              // Só existe em envios feitos depois da correção de entrega; docs
              // antigos não têm contagem e simplesmente não mostram a linha.
              const deliveryText = formatDeliveryCounts(item);
              // Só em notificações que passaram pela fila de agendamento: o
              // horário programado, que numa série é o da ocorrência daquele dia.
              const scheduledDate = toDateSafe(item.scheduledFor);
              return (
                <View key={item.id} style={styles.historyRow}>
                  <View style={styles.historyHeader}>
                    <Text style={styles.historyTitle} numberOfLines={2}>
                      {item.title || 'Sem título'}
                    </Text>
                    {errorText ? (
                      <HoverTooltip label={errorText} placement="bottom" maxWidth={260}>
                        <Badge variant={status.variant}>{status.label}</Badge>
                      </HoverTooltip>
                    ) : (
                      <Badge variant={status.variant}>{status.label}</Badge>
                    )}
                  </View>

                  {item.body ? (
                    <Text style={styles.historyBody} numberOfLines={3}>
                      {item.body}
                    </Text>
                  ) : null}

                  <View style={styles.historyMeta}>
                    <View style={styles.historyMetaItem}>
                      <Ionicons name="calendar-outline" size={13} color={ADMIN_COLORS.muted} />
                      <Text style={styles.historyMetaText}>{formatSendDate(item.sendDate)}</Text>
                    </View>
                    {deliveryText ? (
                      <View style={styles.historyMetaItem}>
                        <Ionicons
                          name="paper-plane-outline"
                          size={13}
                          color={ADMIN_COLORS.muted}
                        />
                        <Text style={styles.historyMetaText}>{deliveryText}</Text>
                      </View>
                    ) : null}
                    {scheduledDate ? (
                      <View style={styles.historyMetaItem}>
                        <Ionicons name="time-outline" size={13} color={ADMIN_COLORS.muted} />
                        <Text style={styles.historyMetaText}>
                          Agendada para {formatScheduleDateTime(scheduledDate)}
                        </Text>
                      </View>
                    ) : null}
                    <View style={styles.historyMetaItem}>
                      <Ionicons name="people-outline" size={13} color={ADMIN_COLORS.muted} />
                      <Text style={styles.historyMetaText} numberOfLines={1}>
                        {describeAudience(item.productsIds)}
                      </Text>
                    </View>
                  </View>
                </View>
              );
            })}
          </View>
        )}
      </SectionCard>
    </View>
  );

  return (
    <Container style={{ backgroundColor: ADMIN_COLORS.surfaceSubtle }}>
      <Header onPress={voltar} user={user} isAdminPage />
      <ScrollView>
        <View style={{ paddingHorizontal: 24, paddingTop: 24 }}>
          <Text style={[styles.pageTitle, isMobile && { fontSize: 24 }]}>Notificações</Text>
          <Text style={styles.pageSubtitle}>
            Envie comunicados para os seus alunos e acompanhe o histórico de envios.
          </Text>

          <View style={styles.tabs}>
            {renderTab('compose', 'Nova notificação')}
            {renderTab(
              'scheduled',
              'Agendadas',
              loadingScheduled ? undefined : scheduledNotifications.length
            )}
            {renderTab('history', 'Enviadas', loadingHistory ? undefined : sentNotifications.length)}
          </View>
        </View>

        {tab === 'history' ? renderHistory() : tab === 'scheduled' ? renderScheduled() : (
        <ContainerLayout style={!isVisible ? { justifyContent: 'center', flexDirection: 'column' } : {}}>
          <ContainerInputs style={!isVisible ? { width: '100%' } : {}}>
            <SectionCard title="Mensagem" subtitle="O que o aluno vê na notificação.">
              <View style={{ marginBottom: 18 }}>
                <Text style={styles.fieldLabel}>Título</Text>
                <Text style={styles.fieldHint}>
                  Um título curto que resume a mensagem.
                </Text>
                <AdminInput
                  placeholder="Título"
                  keyboardType="default"
                  returnKeyType="go"
                  value={title}
                  onChangeText={setTitle}
                  maxLength={MAX_TITLE_LENGTH}
                />
                <Text style={styles.charCount}>
                  {MAX_TITLE_LENGTH - title.length} caracteres restantes
                </Text>
              </View>

              <View>
                <Text style={styles.fieldLabel}>Mensagem</Text>
                <Text style={styles.fieldHint}>Uma mensagem curta com os detalhes.</Text>
                <AdminInput
                  placeholder="Mensagem"
                  keyboardType="default"
                  returnKeyType="go"
                  value={content}
                  onChangeText={setContent}
                  maxLength={MAX_MESSAGE_LENGTH}
                />
                <Text style={styles.charCount}>
                  {MAX_MESSAGE_LENGTH - content.length} caracteres restantes
                </Text>
              </View>
            </SectionCard>

            <SectionCard
              title="Destinatários"
              subtitle="Quem vai receber esta notificação."
            >
              {/* Escolha excludente: os dois checkboxes já se desmarcavam
                  mutuamente na mão, o que é justamente o papel de um radio. */}
              <SegmentedControl
                value={sendFilteredUser ? 'filtered' : sendAllUser ? 'all' : null}
                onChange={(value) => {
                  setSendAllUsers(value === 'all');
                  setSendFilteredUsers(value === 'filtered');
                }}
                options={[
                  {
                    value: 'all',
                    label: 'Todos os alunos',
                    description: 'Envia para toda a base cadastrada.',
                    icon: 'people-outline',
                  },
                  {
                    value: 'filtered',
                    label: 'Por produto',
                    description: 'Só quem tem acesso aos produtos escolhidos.',
                    icon: 'pricetags-outline',
                  },
                ]}
              />

              {sendFilteredUser && (
                <View style={{ marginTop: 16 }}>
                  <FlatListMultipleSelection
                    items={allProducts}
                    setItems={setAllProducts}
                    smallText="Produto(s) selecionado(s):"
                    footerText="Selecione o(s) produto(s) que receberão a notificação"
                    notification={true}
                  />
                </View>
              )}
            </SectionCard>

            <SectionCard
              title="Quando enviar"
              subtitle="Envie agora ou deixe programado para uma data e hora."
            >
              <SegmentedControl
                value={sendMode}
                onChange={(value) => {
                  setSendMode(value);
                  setError('');
                }}
                options={[
                  {
                    value: 'now',
                    label: 'Agora',
                    description: 'O envio começa assim que você confirmar.',
                    icon: 'flash-outline',
                  },
                  {
                    value: 'schedule',
                    label: 'Agendar',
                    description: 'Programe hoje o envio de outro dia.',
                    icon: 'calendar-outline',
                  },
                ]}
              />

              {sendMode === 'schedule' && (
                <View style={{ marginTop: 16, gap: 16 }}>
                  <View style={isVisible ? { flexDirection: 'row', gap: 16 } : { gap: 16 }}>
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <Text style={styles.fieldLabel}>Data</Text>
                      <AdminDatePicker
                        value={scheduleDate}
                        minDate={todayIso()}
                        placeholder="Selecione o dia"
                        onChange={(iso) => {
                          setScheduleDate(iso);
                          setError('');
                        }}
                      />
                    </View>

                    <View style={isVisible ? { width: 160 } : {}}>
                      <Text style={styles.fieldLabel}>Horário</Text>
                      <AdminInput
                        placeholder="09:30"
                        keyboardType="numeric"
                        returnKeyType="done"
                        value={scheduleTime}
                        maxLength={5}
                        onChangeText={(value) => {
                          setScheduleTime(maskTimeInput(value));
                          setError('');
                        }}
                        onBlur={() => setScheduleTime((prev) => normalizeTimeInput(prev))}
                      />
                    </View>
                  </View>

                  <View>
                    <Text style={styles.fieldLabel}>Repetir</Text>
                    <Text style={styles.fieldHint}>
                      Uma série envia a mesma notificação em todas as datas, sem precisar recriá-la.
                    </Text>
                    <View style={styles.chipRow}>
                      {[
                        { value: 'once', label: 'Não se repete' },
                        { value: 'daily', label: 'Todo dia' },
                        { value: 'weekly', label: firstDate ? describeRecurrence('weekly', firstDate) : 'Toda semana' },
                        { value: 'monthly', label: firstDate ? describeRecurrence('monthly', firstDate) : 'Todo mês' },
                      ].map((opcao) => {
                        const ativa = frequency === opcao.value;
                        return (
                          <Pressable
                            key={opcao.value}
                            onPress={() => {
                              setFrequency(opcao.value);
                              if (opcao.value === 'once') setUntilDate('');
                              setError('');
                            }}
                            style={({ hovered }) => [
                              styles.chip,
                              hovered && !ativa && { borderColor: ADMIN_COLORS.accent },
                              ativa && styles.chipActive,
                            ]}
                          >
                            <Text style={[styles.chipText, ativa && styles.chipTextActive]}>
                              {opcao.label}
                            </Text>
                          </Pressable>
                        );
                      })}
                    </View>
                  </View>

                  {nearbyWarning ? (
                    <AdminAlert variant="warn">{nearbyWarning}</AdminAlert>
                  ) : null}

                  {frequency === 'monthly' && firstDate && firstDate.getDate() > 28 ? (
                    <AdminAlert variant="warn">
                      Os meses sem o dia {firstDate.getDate()} são pulados — como no calendário. Para
                      enviar em todos os meses, escolha um dia até o 28.
                    </AdminAlert>
                  ) : null}

                  {frequency !== 'once' && (
                    <View>
                      <Text style={styles.fieldLabel}>Repetir até</Text>
                      <Text style={styles.fieldHint}>
                        Deixe em branco para a série continuar sem data de término.
                      </Text>
                      {untilDate ? (
                        <View style={styles.untilRow}>
                          <View style={{ flex: 1, minWidth: 0 }}>
                            <AdminDatePicker
                              value={untilDate}
                              minDate={scheduleDate || todayIso()}
                              placeholder="Selecione o último dia"
                              onChange={(iso) => {
                                setUntilDate(iso);
                                setError('');
                              }}
                            />
                          </View>
                          <Pressable
                            onPress={() => {
                              setUntilDate('');
                              setError('');
                            }}
                            style={({ hovered }) => [
                              styles.ghostBtn,
                              hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
                            ]}
                          >
                            <Ionicons name="infinite" size={16} color={ADMIN_COLORS.ink} />
                            <Text style={styles.ghostBtnText}>Sem fim</Text>
                          </Pressable>
                        </View>
                      ) : (
                        <Pressable
                          onPress={() => setUntilDate(scheduleDate || todayIso())}
                          style={({ hovered }) => [
                            styles.ghostBtn,
                            { alignSelf: 'flex-start', marginTop: 6 },
                            hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
                          ]}
                        >
                          <Ionicons name="calendar-outline" size={16} color={ADMIN_COLORS.ink} />
                          <Text style={styles.ghostBtnText}>Definir data de término</Text>
                        </Pressable>
                      )}
                    </View>
                  )}

                  {/* Prévia: o admin vê as datas reais antes de confirmar — é o
                      que revela um mês pulado ou um término mal escolhido. */}
                  {previewDates.length > 0 ? (
                    <View style={styles.previewBox}>
                      <Text style={styles.previewTitle}>
                        {frequency === 'once' ? 'Este envio' : 'Próximos envios'}
                      </Text>
                      {previewDates.map((data) => (
                        <Text key={data.getTime()} style={styles.previewItem}>
                          • {formatScheduleDateTime(data)}
                        </Text>
                      ))}
                      {frequency !== 'once' && previewDates.length >= 3 ? (
                        <Text style={styles.previewItem}>
                          • … {untilDate ? `até ${formatScheduleDateTime(new Date(endOfDayMs(untilDate)))}` : 'e assim por diante'}
                        </Text>
                      ) : null}
                    </View>
                  ) : null}

                  <View style={styles.scheduleHintBox}>
                    <Ionicons
                      name="information-circle-outline"
                      size={16}
                      color={ADMIN_COLORS.muted}
                    />
                    <Text style={styles.scheduleHintText}>
                      O horário é o do seu computador. O disparo é verificado a cada minuto, então a
                      notificação pode sair com até um minuto de atraso. Envios marcados para o mesmo
                      horário entram numa fila e saem um após o outro. Dá para excluir o agendamento
                      a qualquer momento na aba "Agendadas".
                    </Text>
                  </View>
                </View>
              )}
            </SectionCard>

            <SectionCard
              title="Destino do clique"
              subtitle="Opcional: para onde o aluno vai ao tocar na notificação."
            >
              <View style={styles.switchRow}>
                <View style={{ flex: 1, minWidth: 0 }}>
                  <Text style={styles.fieldLabel}>Abrir uma tela do aplicativo</Text>
                  <Text style={[styles.fieldHint, { marginBottom: 0 }]}>
                    Informe o link de um conteúdo ou post da comunidade.
                  </Text>
                </View>
                <AdminSwitch value={isDeeplink} onValueChange={setIsDeeplink} />
              </View>

              {isDeeplink && (
                <View style={{ marginTop: 14 }}>
                  <AdminInput
                    placeholder="Cole o link aqui..."
                    keyboardType="default"
                    returnKeyType="go"
                    value={deeplink}
                    onChangeText={(value) => formatDeeplink(value)}
                  />
                </View>
              )}
            </SectionCard>
          </ContainerInputs>

          {/* A prévia sempre aparece: no desktop como coluna ao lado do
              formulário, no celular logo abaixo dele. Antes ela sumia abaixo
              de 1024px — e era justamente no celular que o admin queria
              conferir como a notificação chega. */}
          <ContainerMockup
            style={!isVisible ? { width: '100%', alignItems: 'center' } : {}}
          >
            <MockupSmartphone title={title} content={content} />
          </ContainerMockup>
        </ContainerLayout>
        )}
      </ScrollView>

      {/* Os alertas também aparecem na aba de agendadas: é lá que o resultado
          de um cancelamento precisa ser visto. */}
      {tab !== 'history' && (
        <View style={{ paddingHorizontal: 24 }}>
          {error ? (
            <AdminAlert variant="error" onClose={() => setError('')}>{error}</AdminAlert>
          ) : null}
          {success ? (
            <AdminAlert variant="success" onClose={() => setSuccess('')}>{success}</AdminAlert>
          ) : null}
        </View>
      )}

      {/* O botão de confirmar só faz sentido na aba de composição. */}
      {tab === 'compose' && (
        <View style={styles.footer}>
          <Pressable
            onPress={handleSend}
            disabled={loading}
            style={({ hovered }) => [
              styles.primaryBtn,
              isMobile && { width: '100%' },
              hovered && { backgroundColor: ADMIN_COLORS.accentDark },
              loading && { opacity: 0.7 },
            ]}
          >
            {loading ? (
              <ActivityIndicator size="small" color="#FFFFFF" />
            ) : (
              <>
                <Ionicons
                  name={sendMode === 'schedule' ? 'calendar' : 'send'}
                  size={16}
                  color="#FFFFFF"
                />
                <Text style={styles.primaryBtnText}>
                  {sendMode === 'schedule' ? 'Agendar notificação' : 'Enviar notificação'}
                </Text>
              </>
            )}
          </Pressable>
        </View>
      )}

      {renderDeleteConfirm()}
    </Container>
  );
};

const styles = StyleSheet.create({
  pageTitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 28,
    fontWeight: '700',
    color: ADMIN_COLORS.ink,
  },
  pageSubtitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    color: ADMIN_COLORS.muted,
    marginTop: 4,
  },
  tabs: {
    flexDirection: 'row',
    gap: 6,
    marginTop: 20,
    borderBottomWidth: 1,
    borderBottomColor: ADMIN_COLORS.border,
  },
  tab: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
    cursor: 'pointer',
  },
  tabActive: { borderBottomColor: ADMIN_COLORS.accent },
  tabText: { fontFamily: ADMIN_FONT, fontSize: 14, fontWeight: '600', color: ADMIN_COLORS.muted },
  tabTextActive: { color: ADMIN_COLORS.accent },
  fieldLabel: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.label,
  },
  fieldHint: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    color: ADMIN_COLORS.muted,
    marginTop: 2,
    marginBottom: 8,
    lineHeight: 18,
  },
  charCount: {
    fontFamily: ADMIN_FONT,
    fontSize: 12,
    color: ADMIN_COLORS.muted,
    textAlign: 'right',
    marginTop: 6,
  },
  switchRow: { flexDirection: 'row', alignItems: 'center', gap: 12, marginTop: 4 },
  emptyText: { fontFamily: ADMIN_FONT, fontSize: 14, color: ADMIN_COLORS.muted, lineHeight: 20 },
  scheduleHintBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    borderRadius: 10,
    padding: 12,
    backgroundColor: ADMIN_COLORS.surfaceSubtle,
  },
  scheduleHintText: {
    flex: 1,
    minWidth: 0,
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    color: ADMIN_COLORS.muted,
    lineHeight: 18,
  },
  dayHeading: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    fontWeight: '700',
    color: ADMIN_COLORS.label,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  scheduledActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 12,
  },
  chipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 4 },
  chip: {
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: 999,
    paddingVertical: 8,
    paddingHorizontal: 14,
    backgroundColor: ADMIN_COLORS.surface,
    cursor: 'pointer',
  },
  chipActive: {
    borderColor: ADMIN_COLORS.accent,
    backgroundColor: `${ADMIN_COLORS.accent}14`,
  },
  chipText: { fontFamily: ADMIN_FONT, fontSize: 13, fontWeight: '600', color: ADMIN_COLORS.muted },
  chipTextActive: { color: ADMIN_COLORS.accent },
  untilRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  previewBox: {
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: 10,
    padding: 12,
    gap: 4,
  },
  previewTitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 12.5,
    fontWeight: '700',
    color: ADMIN_COLORS.label,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 2,
  },
  previewItem: { fontFamily: ADMIN_FONT, fontSize: 13.5, color: ADMIN_COLORS.ink },
  dangerBtn: { borderColor: ADMIN_COLORS.dangerBorder },
  modalBackdrop: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
    backgroundColor: 'rgba(17, 24, 39, 0.45)',
  },
  modalCard: {
    width: '100%',
    maxWidth: 460,
    borderRadius: 14,
    padding: 22,
    backgroundColor: ADMIN_COLORS.surface,
  },
  modalTitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 18,
    fontWeight: '700',
    color: ADMIN_COLORS.ink,
  },
  modalText: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    color: ADMIN_COLORS.muted,
    lineHeight: 20,
    marginTop: 10,
  },
  modalActions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 22,
  },
  dangerSolidBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    minHeight: 40,
    borderRadius: 8,
    paddingHorizontal: 18,
    backgroundColor: ADMIN_COLORS.danger,
    cursor: 'pointer',
  },
  dangerSolidBtnText: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  historyRow: {
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: 10,
    padding: 14,
    backgroundColor: ADMIN_COLORS.surface,
  },
  historyHeader: { flexDirection: 'row', alignItems: 'flex-start', gap: 12 },
  historyTitle: {
    flex: 1,
    minWidth: 0,
    fontFamily: ADMIN_FONT,
    fontSize: 15,
    fontWeight: '700',
    color: ADMIN_COLORS.ink,
  },
  historyBody: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    color: ADMIN_COLORS.muted,
    lineHeight: 20,
    marginTop: 6,
  },
  historyMeta: { flexDirection: 'row', flexWrap: 'wrap', gap: 16, marginTop: 10 },
  historyMetaItem: { flexDirection: 'row', alignItems: 'center', gap: 5, maxWidth: '100%' },
  historyMetaText: { fontFamily: ADMIN_FONT, fontSize: 12.5, color: ADMIN_COLORS.muted },
  ghostBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: 8,
    paddingVertical: 7,
    paddingHorizontal: 12,
    cursor: 'pointer',
  },
  ghostBtnText: { fontFamily: ADMIN_FONT, fontSize: 13, fontWeight: '600', color: ADMIN_COLORS.ink },
  footer: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: 24,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.border,
    backgroundColor: ADMIN_COLORS.surface,
  },
  primaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    minHeight: 44,
    borderRadius: 10,
    paddingHorizontal: 24,
    backgroundColor: ADMIN_COLORS.accent,
    cursor: 'pointer',
  },
  primaryBtnText: { fontFamily: ADMIN_FONT, fontSize: 14, fontWeight: '600', color: '#FFFFFF' },
});

export default Notifications;
